/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './ActiveUsers';

const ActiveUsersManager: ComponentManagerFn<'ActiveUsers'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'ActiveUsers'>(platform, 'ActiveUsers'),
    type: 'ActiveUsers',
    name: 'Active Users',
    defaultValue,
    props: {
      max: {
        label: 'Max Users Displayed',
        type: 'number',
        min: 2,
        max: 10,
      },
      size: {
        label: 'Avatar Size',
        type: 'number',
        min: 20,
        max: 40,
        step: 2,
      },
    },
    category: 'Page Header',
    icon: { iconName: 'users' },
    description: `**Overview**<br/>
The Active Users component is a collaboration indicator designed to display the real-time presence of other users currently viewing the same page or record. It typically renders as a row of overlapping user avatars (profile pictures or initials).
**Note:** The standard application header usually includes this functionality by default. You should only use this component if you are building a **Custom Header** or a specialized dashboard and need to replicate that presence indicator manually.

**1. Configuration**<br/>
You can control the visual density and size of the component to ensure it fits seamlessly into your custom navigation or layout.
* **Max Users Displayed:** Determines the maximum number of individual avatars to show in the row before grouping the remainder into an overflow indicator (e.g., "+3 others").
    * **Range:** 2 to 10 users.
* **Avatar Size:** Controls the diameter (in pixels) of the user icons.
    * **Range:** 20px (small/compact for dense headers) to 40px (large/prominent for focused dashboards).

**2. Best Use Cases**<br/>
* **Custom Headers:** Replicating standard collaboration features when designing a completely custom-built page header or navigation bar.
* **Collaborative Forms:** Placing this at the top of a specific record view (like an Incident or Project) to warn users that others are currently viewing or potentially editing the same data, helping to prevent "last-write-wins" conflicts.
* **Admin Monitoring:** Allowing a supervisor to see at a glance who is currently active on a specific operational dashboard or live tracking map.
* **Shared Workspaces:** Enhancing the feeling of a "live" environment in applications where teamwork and real-time coordination are essential.`,
  };
};

export default ActiveUsersManager;
